// Third-party
import React, { Component } from 'react';
import Highcharts, { SeriesPieOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';
import chroma from 'chroma-js';
import ChartLegend from './ChartLegend';
import * as ProposalNS from '../../../modules/proposal/Proposal';

// Types
import Nullable from '@/types/nullable';
import Dictionary from '@/types/dictionary';
import SeriesData from '@/types/series-data';
import Locale from '@/types/locale';

// Utils
import { convertConfigOptionsToHighcharts } from '../../../utils/charts';

export type DonutChartOptions = Highcharts.Options;

type Props = {
  data: SeriesData[][];
  configOptions: Partial<ProposalNS.DonutChartConfigOptions>;
  showLegend: boolean;
  showTotal: boolean;
  className?: string;
  dark?: boolean;
  colorsDict: Nullable<Dictionary<string>>;
  locale?: Locale;
  title?: Highcharts.TitleOptions;
};

type State = {
  chartOptions: DonutChartOptions;
};

class ProposalDonutChart extends Component<Props, State> {
  private defaultColorScale = chroma.scale(['#0e4263', '#0587da', '#11e9f7', '#f34190', '#f5994c', 'yellow', '#157c55']);

  static defaultProps: Props = {
    colorsDict: null,
    configOptions: {},
    data: [],
    showLegend: false,
    showTotal: false,
  };

  readonly state: State = {
    chartOptions: convertConfigOptionsToHighcharts('pie', this.props.configOptions, this.props.locale),
  };

  componentDidMount() {
    this.updateChart();
  }

  componentDidUpdate(prevProps: Props) {
    const { data } = this.props;

    if (prevProps.data !== data) {
      this.updateChart();
    }
  }

  getDataColor = (data: SeriesData[], index: number) => {
    const { colorsDict } = this.props;

    if (colorsDict) {
      const datum = data[index];

      if (datum.key && colorsDict[datum.key]) {
        return colorsDict[datum.key];
      }

      if (colorsDict[datum.name]) {
        return colorsDict[datum.name];
      }

      if (colorsDict[index]) {
        return colorsDict[index];
      }
    }
    return this.defaultColorScale(index / data.length).hex();
  };

  updateChart() {
    const { data, configOptions, title } = this.props;
    const dataWithColors = data.map(series =>
      series.map((d: SeriesData, i: number) => ({
        ...d,
        y: Number(d.y),
        color: this.getDataColor(series, i),
      })),
    );

    const series = dataWithColors.map((dataSeries, index) => {
      if (configOptions.series && configOptions.series[index]) {
        return {
          data: dataSeries,
          ...configOptions.series[index],
        };
      }

      return {
        data: dataSeries,
        type: 'pie',
      };
    }) as SeriesPieOptions[];

    const updatedOptions = { ...configOptions, title, series } as DonutChartOptions;

    this.setState({ chartOptions: updatedOptions });
  }

  render() {
    const { dark, showLegend, showTotal, locale } = this.props;
    const { chartOptions } = this.state;

    return (
      <div id="chart" className={classNames('donut-chart', { dark })}>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />

        {showLegend && <ChartLegend className="donut-legend" chartOptions={chartOptions} sort={false} showTotal={showTotal} locale={locale} />}
      </div>
    );
  }
}

export default ProposalDonutChart;
